import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Icon,
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  Button,
  Switch,
} from "../../";
import { useQueryClient } from "@tanstack/react-query";
import { useToggleDriverCompany, useInstallPlugin } from "../../../../hooks";
import { usePostHog, useIntercom } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";

const DeliveryServiceCard = ({ driverCompany }) => {
  const { t } = useTranslation("integrations");
  const navigate = useNavigate();
  const { isFeatureEnabled } = usePostHog();
  const { showMessenger } = useIntercom();
  const deliveryServicesConfigureEnabled = isFeatureEnabled(
    "delivery_services_configure_enabled",
  );
  const {
    id,
    name,
    logo,
    enabled,
    plugin_id,
    plugin_root,
    plugin,
    driver_groups,
  } = driverCompany;

  const queryClient = useQueryClient();
  const [isEnabled, setIsEnabled] = useState(enabled);
  const toggleMutation = useToggleDriverCompany();
  const installMutation = useInstallPlugin();

  useEffect(() => {
    setIsEnabled(enabled);
  }, [enabled]);

  const hasPlugin = !!plugin_id;
  const canInstall = !hasPlugin && !!plugin_root;

  const driverGroupId = driver_groups?.[0]?.id;

  const handleToggle = async (checked) => {
    setIsEnabled(checked);
    try {
      await toggleMutation.mutateAsync({
        driverId: id,
        enabled: checked,
      });
    } catch {
      setIsEnabled(!checked);
    }
  };

  const handleCardClick = () => {
    // Use driverGroupId if available, otherwise use the delivery service id
    const navigationId = driverGroupId || id;
    navigate(`/integrations/delivery-services/${navigationId}`, {
      state: {
        plugin,
        isDriverCompanyId: !driverGroupId, // Flag to indicate this is a driverCompanyId, not driverGroupId
        driverCompany: !driverGroupId ? driverCompany : null, // Pass driverCompany data if no driverGroupId
      },
    });
  };

  const handleConfigure = async (e) => {
    e.stopPropagation();
    // Auto-install plugin if not installed
    if (canInstall) {
      const response = await installMutation.mutateAsync({
        pluginRoot: plugin_root,
      });
      const installedPlugin = response?.result;

      // Update driverCompanies cache so the list shows "Installed" when navigating back
      queryClient.setQueriesData(
        { queryKey: ["driverCompanies"] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            driverCompanies: oldData.driverCompanies.map((dc) =>
              dc.id === id
                ? {
                    ...dc,
                    plugin_id: installedPlugin?.id,
                    plugin: installedPlugin,
                  }
                : dc,
            ),
          };
        },
      );

      // Navigate with the fresh plugin data
      const updatedCompany = {
        ...driverCompany,
        plugin_id: installedPlugin?.id,
        plugin: installedPlugin,
      };
      const navigationId = driverGroupId || id;
      navigate(`/integrations/delivery-services/${navigationId}`, {
        state: {
          plugin: installedPlugin,
          isDriverCompanyId: !driverGroupId,
          driverCompany: !driverGroupId ? updatedCompany : null,
        },
      });
      return;
    }
    handleCardClick();
  };

  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-800 bg-gray-900 p-6 transition-colors hover:bg-gray-800/50">
      {/* Header */}
      <div className="flex-shrink-0">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex flex-1 items-center space-x-3">
            {logo ? (
              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-700 bg-gray-800 p-2">
                <img
                  src={logo}
                  alt={name}
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="flex hidden h-full w-full items-center justify-center bg-gray-700 text-lg font-semibold text-white">
                  {name.charAt(0)}
                </div>
              </div>
            ) : (
              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800">
                <Icon name="Truck" className="h-8 w-8 text-gray-400" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="mb-1 text-lg font-semibold text-white">{name}</h3>
              {!driverGroupId && deliveryServicesConfigureEnabled ? (
                <HoverCard openDelay={300} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/delivery/groups");
                      }}
                      className="group inline-flex cursor-pointer items-center space-x-1.5 rounded-full border border-yellow-500/30 bg-yellow-900/20 px-2 py-0.5 text-xs font-medium text-yellow-400 transition-colors hover:bg-yellow-900/30"
                    >
                      <Icon
                        name="AlertCircle"
                        className="h-3.5 w-3.5 flex-shrink-0"
                      />
                      <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">
                        Delivery Group Required
                      </span>
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent
                    side="top"
                    className="w-72 border border-gray-700 bg-gray-800 p-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <Icon
                          name="Info"
                          className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-400"
                        />
                        <div>
                          <p className="mb-1 text-sm font-medium text-white">
                            Delivery Group Required
                          </p>
                          <p className="text-xs leading-relaxed text-gray-300">
                            This delivery service needs to be configured with a
                            Delivery Group to enable features like assignment
                            settings and business management.
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/delivery/groups");
                        }}
                        className="mt-2 flex w-full items-center justify-center bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700"
                      >
                        <Icon name="Settings" className="mr-1.5 h-3 w-3" />
                        Configure Delivery Group
                      </Button>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              ) : hasPlugin ? (
                <span className="inline-flex items-center rounded-full border border-blue-500/30 bg-blue-900/20 px-2 py-0.5 text-xs font-medium text-blue-400">
                  Installed
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-auto flex-shrink-0 space-y-3 border-t border-gray-800 pt-4">
        {deliveryServicesConfigureEnabled ? (
          <>
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-300">
                {isEnabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={isEnabled}
                onCheckedChange={handleToggle}
                disabled={toggleMutation.isPending}
              />
            </div>

            {/* Configure Button */}
            <button
              onClick={handleConfigure}
              disabled={installMutation.isPending}
              className="flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {installMutation.isPending ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  <span>Installing...</span>
                </>
              ) : (
                <span>{t("actions.configure")}</span>
              )}
            </button>
          </>
        ) : (
          <button
            onClick={() => showMessenger()}
            className="flex w-full items-center justify-center rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-600"
          >
            <Icon name="MessageCircle" className="mr-2 h-4 w-4" />
            <span>{t("contactUs")}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default DeliveryServiceCard;
